/* Atlas cart drawer (sections/atlas-cart-drawer.liquid), ported from ac-cat-v2 cart-drawer.js.
   Opens from the header cart buttons and atlas:cart:open, refreshes itself on Pitch's cart:update event through
   the Section Rendering API, and opens after a Pitch add to cart (CartAddEvent) when the section setting allows. */
import { CartAddEvent } from '@theme/events';

if (!window.__atlasCartDrawer) {
  window.__atlasCartDrawer = true;

  const root = () => document.querySelector('[data-cart-drawer]');
  const routes = () => (window.Theme && window.Theme.routes) || {};
  let lastFocus = null;
  let pending = null;

  function lock(on) {
    document.body.classList.toggle('atlas-lock', on);
  }

  async function open() {
    const el = root();
    if (!el) return;
    if (pending) await pending.catch(() => {});
    if (window.__atlasCloseMnav) window.__atlasCloseMnav();
    if (!el.hidden) return;
    lastFocus = document.activeElement;
    el.hidden = false;
    lock(true);
    const close = el.querySelector('.ac__close');
    if (close) close.focus({ preventScroll: true });
  }

  function close() {
    const el = root();
    if (!el || el.hidden) return;
    el.hidden = true;
    lock(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  /* Free shipping for a cart whose currency has no amount of its own: convert the default amount (shop currency)
     at Shopify's rate and fill in the message and bar the section left for it. */
  function convertShip(scope) {
    const box = scope && scope.querySelector('[data-ship-convert]');
    const cur = window.Shopify && window.Shopify.currency;
    if (!box || !cur || !cur.rate || cur.active !== box.dataset.cur) return;
    const unit = Math.ceil((parseInt(box.dataset.base, 10) / 100) * parseFloat(cur.rate));
    const thr = unit * 100;
    const sub = parseInt(box.dataset.sub, 10) || 0;
    const count = parseInt(box.dataset.count, 10) || 0;
    if (!thr) return;
    const fmt = (cents) => {
      if (window.Atlas && window.Atlas.money) return window.Atlas.money(cents);
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur.active, maximumFractionDigits: 0 }).format(cents / 100); }
      catch (e) { return cur.active + ' ' + Math.round(cents / 100); }
    };
    const b = (cents) => '<b>' + fmt(cents) + '</b>';
    const done = sub >= thr;
    let msg;
    if (!count) msg = box.dataset.msgEmpty.replace('[amount]', b(thr));
    else if (done) msg = box.dataset.msgUnlocked;
    else msg = box.dataset.msgAway.replace('[amount]', b(thr - sub));
    box.innerHTML = '<span></span>';
    box.firstChild.innerHTML = msg;
    box.classList.toggle('is-done', done);
    if (count) {
      const top = box.closest('.ac__top');
      if (top && !top.querySelector('.ac__bar')) {
        const pct = Math.min(100, (sub / thr) * 100);
        const bar = document.createElement('div');
        bar.className = 'ac__bar';
        bar.setAttribute('role', 'progressbar');
        bar.setAttribute('aria-valuemin', '0');
        bar.setAttribute('aria-valuemax', '100');
        bar.setAttribute('aria-valuenow', String(Math.round(pct)));
        bar.innerHTML = '<span style="width:' + pct + '%"></span>';
        top.appendChild(bar);
      }
    }
  }
  convertShip(root());

  /* Swap in fresh drawer markup. html is the section's rendered HTML; without it, fetch it. */
  function refresh(html) {
    const el = root();
    if (!el) return Promise.resolve();
    const id = el.dataset.sectionId;
    const get = html
      ? Promise.resolve(html)
      : fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}?sections=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((json) => json[id]);
    pending = get
      .then((markup) => {
        if (!markup) return;
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const fresh = doc.querySelector('[data-cart-panel]');
        const panel = el.querySelector('[data-cart-panel]');
        if (fresh && panel) {
          const hadFocus = panel.contains(document.activeElement);
          /* keep the socks strip where it was and let the shipping bar glide from its old width */
          const oldStrip = panel.querySelector('[data-socks-scroll]');
          const stripLeft = oldStrip ? oldStrip.scrollLeft : 0;
          const oldBar = panel.querySelector('.ac__bar span');
          const barFrom = oldBar ? oldBar.style.width : null;
          panel.innerHTML = fresh.innerHTML;
          convertShip(panel);
          const strip = panel.querySelector('[data-socks-scroll]');
          if (strip && stripLeft) strip.scrollLeft = stripLeft;
          const bar = panel.querySelector('.ac__bar span');
          if (bar && barFrom) {
            const to = bar.style.width;
            bar.style.transition = 'none';
            bar.style.width = barFrom;
            void bar.offsetWidth;
            bar.style.transition = '';
            bar.style.width = to;
          }
          if (hadFocus) {
            const c = panel.querySelector('.ac__close');
            if (c) c.focus({ preventScroll: true });
          }
        }
      })
      .finally(() => {
        pending = null;
        el.classList.remove('is-busy');
      });
    return pending;
  }

  /* Shopify's reason when a change is refused (e.g. not enough stock), shown at the top of the drawer */
  function showMsg(text) {
    const m = document.querySelector('[data-cart-msg]');
    if (!m) return;
    m.textContent = text || 'Sorry, that change couldn\'t be made. Please try again.';
    m.hidden = false;
  }

  /* Change a line's quantity (0 removes it), then tell the rest of the theme (cart count, cart page) through cart:update */
  function setLine(line, quantity) {
    const el = root();
    const id = el && el.dataset.sectionId;
    if (el) el.classList.add('is-busy');
    return fetch(`${routes().cart_change_url || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity, sections: id })
    })
      .then((r) => r.text().then((text) => {
        let body = {};
        try { body = text ? JSON.parse(text) : {}; } catch (e) { body = {}; }
        if (!r.ok || body.status) throw body;
        return body;
      }))
      .then((cart) => {
        const sections = cart.sections || {};
        delete cart.sections;
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'atlas-cart-drawer',
            data: { source: 'atlas-cart-drawer', itemCount: cart.item_count, sections }
          }
        }));
      })
      .catch((err) => {
        if (el) el.classList.remove('is-busy');
        return refresh().then(() => showMsg(err && err.description));
      });
  }

  document.addEventListener('atlas:cart:open', open);
  document.addEventListener('atlas:cart:close', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  document.addEventListener('click', (e) => {
    const el = root();
    if (!el) return;

    /* Header cart buttons: open the drawer (the cart page keeps its own page) */
    const opener = e.target.closest('[data-cart-open]');
    if (opener && el.dataset.cartPage !== 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
      open();
      return;
    }
    if (!el.contains(e.target)) return;

    const closeBtn = e.target.closest('[data-cart-close]');
    if (closeBtn) {
      /* the scrim closes only when clicked outside the panel */
      if (closeBtn.classList.contains('ac__scrim') && e.target.closest('[data-cart-panel]')) {
        /* fall through to the panel buttons below */
      } else {
        close();
        return;
      }
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      remove.disabled = true;
      setLine(Number(remove.dataset.cartRemove), 0);
      return;
    }
    const qty = e.target.closest('[data-cart-qty]');
    if (qty) {
      qty.disabled = true;
      setLine(Number(qty.dataset.cartQty), Math.max(0, Number(qty.dataset.qty)));
      return;
    }
    /* socks with sizes: open that sock's size picker (one at a time) */
    const tog = e.target.closest('[data-sock-toggle]');
    if (tog) {
      const pick = el.querySelector('[data-sock-picker="' + tog.dataset.sockToggle + '"]');
      const opening = !!pick && pick.hidden;
      el.querySelectorAll('[data-sock-picker]').forEach((x) => { x.hidden = true; });
      el.querySelectorAll('[data-sock-toggle]').forEach((x) => x.setAttribute('aria-expanded', 'false'));
      if (opening) {
        pick.hidden = false;
        tog.setAttribute('aria-expanded', 'true');
        const first = pick.querySelector('button:not(:disabled)');
        if (first) first.focus({ preventScroll: true });
      }
      return;
    }
    const offer = e.target.closest('[data-cart-offer]');
    if (offer && window.Atlas && window.Atlas.cart) {
      offer.disabled = true;
      offer.classList.add('is-loading');
      el.classList.add('is-busy');
      window.Atlas.cart.add([{ id: Number(offer.dataset.cartOffer), quantity: 1 }]).catch((err) => {
        offer.disabled = false;
        offer.classList.remove('is-loading');
        el.classList.remove('is-busy');
        showMsg(err && err.description);
      });
    }
  }, true);

  /* Any cart change in the theme: refresh, and open after an add to cart */
  document.addEventListener('cart:update', (e) => {
    const el = root();
    if (!el) return;
    const d = e.detail || {};
    const data = d.data || {};
    if (data.didError) return;
    const own = data.sections && data.sections[el.dataset.sectionId];
    const done = refresh(own || null);
    if (e instanceof CartAddEvent && el.dataset.openOnAdd === 'true' && el.dataset.cartPage !== 'true') {
      done.then(open);
    }
  });

  /* Pitch's own drawer stays out of the way: it no longer opens by itself after an add */
  const pitchDrawer = document.querySelector('cart-drawer-component[auto-open]');
  if (pitchDrawer && root()) pitchDrawer.removeAttribute('auto-open');
}
